import { motion } from 'framer-motion';
import { 
  Bot, ArrowRight, CheckCircle, Phone, 
  Mail, MessageSquare, Share2, Search
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';

const CALENDLY_URL = "https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min";

const agents = [
  { icon: Phone, title: 'Standard telephonique IA', desc: 'Repond aux appels 24h/24, qualifie les demandes et prend des rendez-vous' },
  { icon: Mail, title: 'Gestion emails', desc: 'Trie, analyse et repond automatiquement a tes emails professionnels' },
  { icon: MessageSquare, title: 'Service client', desc: 'Gère les messages sur tous tes canaux : SMS, messagerie, réseaux sociaux' },
  { icon: Share2, title: 'Marketing digital', desc: 'Publie sur tes réseaux sociaux, crée du contenu et engage ta communauté' },
  { icon: Search, title: 'Prospection', desc: 'Identifie et contacte des prospects qualifies sur LinkedIn' },
];

const benefits = [
  "Disponible 24h/24, 7j/7",
  "Ne rate jamais un appel",
  "Repond instantanement",
  "Travaille pendant que tu dormez",
];

export default function AutomatisationPage() {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Automatisation IA | Agents Intelligents pour Artisans - BatiAssist"
        description="Automatise ton entreprise avec l'IA : standard téléphonique 24/7, gestion emails, service client automatisé, marketing digital et prospection LinkedIn."
        keywords="automatisation IA artisan, agent IA bâtiment, standard téléphonique IA, marketing automatisé BTP, chatbot artisan"
        path="/fonctionnalites/automatisation-ia"
      />
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-ba-secondary/10 text-ba-secondary text-xs font-semibold uppercase tracking-wider rounded-full mb-6">
              <Bot className="w-4 h-4" />
              Automatisation & IA
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-800 mb-6 leading-tight">
              Une equipe digitale{' '}
              <span className="text-ba-secondary">qui travaille pour toi</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto mb-10 leading-relaxed">
              7 agents IA specialises qui travaillent 24h/24 pour gerer tes appels, 
              tes emails, ton marketing et ta prospection.
            </p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-ba-secondary hover:bg-ba-secondary/90 text-white font-semibold rounded-full px-8 py-6 text-lg shadow-lg">
                Decouvrir les agents IA
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Agents */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              Vos agents IA
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Chaque agent est specialise dans une tache specifique
            </p>
          </motion.div>

          <div className="space-y-6">
            {agents.map((agent, index) => {
              const Icon = agent.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-6 p-6 bg-slate-50 rounded-2xl hover:bg-slate-100 transition-colors"
                >
                  <div className="w-14 h-14 bg-ba-secondary rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">{agent.title}</h3>
                    <p className="text-slate-600">{agent.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 24/7 */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
                Disponible 24h/24, 7j/7
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Vos agents IA ne dorment jamais. Ils repondent aux appels a minuit, 
                traitent les emails le dimanche et publient sur tes réseaux sociaux pendant tes vacances.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-ba-success" />
                    <span className="text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-ba-secondary to-orange-600 rounded-3xl p-8"
            >
              <div className="bg-white rounded-2xl p-6 shadow-xl">
                <div className="text-center mb-6">
                  <p className="text-6xl font-bold text-ba-primary mb-2">24/7</p>
                  <p className="text-slate-600">Disponibilite totale</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-ba-success mb-1">100%</p>
                    <p className="text-xs text-slate-500">Appels repondus</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-ba-success mb-1">&lt;30s</p>
                    <p className="text-xs text-slate-500">Temps de reponse</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              Comment ca fonctionne ?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: 1, title: 'Configuration', desc: 'On configure tes agents selon tes besoins et ton activité' },
              { step: 2, title: 'Activation', desc: 'Vos agents commencent a travailler immediatement' },
              { step: 3, title: 'Reporting', desc: 'Tu reçois des rapports sur leur activite' },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="text-center"
              >
                <div className="w-14 h-14 bg-ba-primary rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-bold text-slate-800 mb-2 text-lg">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-ba-secondary">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Automatisez ton entreprise
            </h2>
            <p className="text-lg text-white/80 mb-8">
              Laissez les agents IA gerer les taches repetitives pendant que tu te concentres sur l'essentiel.
            </p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-white hover:bg-white/90 text-ba-secondary font-bold rounded-full px-10 py-7 text-lg shadow-xl">
                Decouvrir les agents IA
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
